import { clsx } from 'clsx'
import { PropsWithChildren, useCallback } from 'react'
import styles from './Button.module.scss'
import { Spinner } from './Spinner'

export type ButtonVariant = 'default' | 'bordered' | 'bordered-gray' | 'secondary' | 'tertiary' | 'green' | 'error'

interface ButtonProps extends PropsWithChildren {
  variant?: ButtonVariant
  size?: 'default' | 'small' | 'icon'
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  isDisabled?: boolean
  isLoading?: boolean
  className?: string
  title?: string
  type?: 'button' | 'submit'
}

export const Button = ({
  type = 'button',
  variant = 'default',
  size = 'default',
  onClick,
  isDisabled,
  isLoading,
  className,
  title,
  children
}: ButtonProps) => {
  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled || isLoading) {
        return
      }

      onClick?.(e)
    },
    [isDisabled, isLoading, onClick]
  )

  return (
    <button
      type={type}
      className={clsx(
        styles.button,
        'justify-content transition-dh-all relative flex cursor-pointer items-center justify-center rounded-lg px-4 whitespace-nowrap select-none',
        variant === 'bordered' && styles.bordered,
        variant === 'bordered-gray' && styles.borderedGray,
        variant === 'secondary' && styles.secondary,
        variant === 'tertiary' && styles.tertiary,
        variant === 'green' && 'bg-green! border-green! hover:border-green-hover! hover:bg-green-hover! text-white',
        variant === 'error' && 'bg-red! border-red! hover:brightness-110 text-white',
        size === 'default' ? 'h-14' : `h-10 ${styles.small}`,
        size === 'icon' && 'w-10',
        'text-[16px]/[24px] font-semibold',
        className
      )}
      onClick={handleOnClick}
      disabled={isDisabled || isLoading}
      title={title}
    >
      {isLoading ? <Spinner size="small" /> : children}
    </button>
  )
}
